import { useCallback, useState, useEffect, useRef } from 'react';
import { CloudArrowUpIcon, XMarkIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { Button, Progress, Alert } from '@/components/ui';
import { useVideoUpload } from '@/hooks';
import { validateVideoDuration } from '@/utils/videoValidation';
import { formatFileSize } from '@/utils/formatters';
import { VIDEO_CONSTRAINTS, TIMEOUTS } from '@/utils/constants';
import { cn } from '@/utils/cn';
import { getVideo } from '@/services/videoApi';
import { useVideoSession } from '@/contexts/VideoSessionContext';
import type { Video, VideoApiResponse } from '@/types/api';

interface VideoUploaderProps {
  onUploadComplete?: (videoId: string, metadata?: VideoApiResponse | Video) => void;
  onUpload?: (file: File) => void; // For file selection callback (test compatibility)
  maxSize?: number;
  accept?: string;
  showProgress?: boolean;
  onProgress?: (progress: number) => void;
  disabled?: boolean;
  className?: string;
  videoType?: 'template' | 'athlete';
}

function VideoUploader({
  onUploadComplete,
  onUpload,
  maxSize,
  accept,
  showProgress = false,
  onProgress,
  disabled = false,
  className,
  videoType = 'athlete',
}: VideoUploaderProps) {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [validationError, setValidationError] = useState<string>('');
  const [isDragging, setIsDragging] = useState(false);
  // Track the filename after a successful upload so we can show the success
  // state instead of jumping back to the drop zone.
  const [uploadedFileName, setUploadedFileName] = useState<string | null>(null);

  // Persist uploaded athlete video File so it can be played back after the server
  // deletes the ephemeral file post-processing (Phase 3: Ephemeral Video Pipeline)
  const { registerUploadedFile } = useVideoSession();

  // Keep a stable ref to the selected file so the onSuccess closure can access it
  // even after setSelectedFile(null) is called at the start of the success handler
  const selectedFileRef = useRef<File | null>(null);

  const maxFileSize = maxSize || VIDEO_CONSTRAINTS.MAX_FILE_SIZE;
  const acceptedFormats = accept || VIDEO_CONSTRAINTS.SUPPORTED_FORMATS.join(',');

  const { upload, isUploading, uploadProgress, error, data: _data, reset } = useVideoUpload({
    videoType,
    onSuccess: async (response) => {
      // Capture the file reference before clearing state
      const uploadedFile = selectedFileRef.current;
      // Record the filename for the success state before clearing selectedFile.
      setUploadedFileName(uploadedFile?.name ?? null);
      setSelectedFile(null);
      selectedFileRef.current = null;

      // The backend returns { message, video: VideoMetadata, pose_extraction }
      // Extract video_id and metadata from response
      let videoId: string;
      let metadata: VideoApiResponse | null = null;

      // Check if response has the new structure with 'video' field
      if ('video' in response && response.video) {
        videoId = response.video.video_id;
        if (!videoId) {
          throw new Error('Upload response missing video_id — backend contract broken');
        }
        metadata = response.video;
      } else if ('video_id' in response && response.video_id) {
        // Legacy format
        videoId = response.video_id;
      } else {
        console.error('Unexpected upload response format:', response);
        return;
      }

      // Register the uploaded file in VideoSessionContext so it can be played back
      // after the server deletes the ephemeral athlete video post-processing
      if (uploadedFile) {
        registerUploadedFile(videoId, uploadedFile);
      }

      // If we have metadata from upload, use it directly
      if (metadata) {
        onUploadComplete?.(videoId, metadata);
      } else {
        // Otherwise fetch metadata using service layer
        try {
          const fetchedMetadata = await getVideo(videoId);
          onUploadComplete?.(videoId, fetchedMetadata);
        } catch (err) {
          console.error('Failed to fetch video metadata:', err);
          onUploadComplete?.(videoId);
        }
      }
    },
  });

  const handleFileSelect = useCallback((file: File) => {
    if (disabled) return;

    setValidationError('');

    // Custom validation with maxSize support
    if (file.size > maxFileSize) {
      const errorMsg = `File is too large. Maximum size is ${formatFileSize(maxFileSize)}`;
      setValidationError(errorMsg);
      return;
    }

    // Validate file type
    const supportedFormats = acceptedFormats.split(',').map(f => f.trim());
    if (!supportedFormats.some(format => file.type.includes(format.replace('video/', '')))) {
      const errorMsg = 'Invalid file type. Please upload a video file.';
      setValidationError(errorMsg);
      return;
    }

    // Check video duration before committing the file — async but kept inside the
    // synchronous callback by running the check in a self-contained async IIFE.
    (async () => {
      try {
        await validateVideoDuration(file);
      } catch (err) {
        setValidationError(err instanceof Error ? err.message : 'Video duration check failed.');
        return;
      }

      setSelectedFile(file);
      selectedFileRef.current = file;

      // Call onUpload callback if provided (for test compatibility and simple usage)
      try {
        onUpload?.(file);
      } catch (err) {
        setValidationError('Upload failed: ' + (err instanceof Error ? err.message : 'Unknown error'));
      }
    })();
  }, [disabled, maxFileSize, acceptedFormats, onUpload]);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleBrowseClick = () => {
    fileInputRef.current?.click();
  };

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);

      if (disabled) return;

      const file = e.dataTransfer.files[0];
      if (file) {
        handleFileSelect(file);
      }
    },
    [handleFileSelect, disabled]
  );

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    if (!disabled) {
      setIsDragging(true);
    }
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    if (!disabled) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleUpload = () => {
    if (selectedFile) {
      upload(selectedFile);
    }
  };

  const handleCancel = () => {
    setSelectedFile(null);
    selectedFileRef.current = null;
    setValidationError('');
    setUploadedFileName(null);
    // Reset the React Query mutation so stale error/data state doesn't linger
    // when the user picks a new file after a failed or successful upload.
    reset();
  };

  // Clear error after timeout
  useEffect(() => {
    if (validationError) {
      const timer = setTimeout(() => {
        setValidationError('');
      }, TIMEOUTS.ERROR_MESSAGE_DISPLAY);
      return () => clearTimeout(timer);
    }
  }, [validationError]);

  // Report progress to callback
  useEffect(() => {
    if (onProgress && isUploading) {
      onProgress(uploadProgress);
    }
  }, [uploadProgress, isUploading, onProgress]);

  // Derive the current display state so the JSX stays clean.
  // Priority: error > uploaded > uploading > file-selected > idle
  const isUploaded = !!uploadedFileName && !selectedFile && !isUploading && !error;

  return (
    <div className={cn('w-full', className)}>
      {/* ── Uploaded success state ─────────────────────────────────────────── */}
      {isUploaded ? (
        <div className="border border-success/30 bg-success/10 rounded-lg p-6">
          <div className="flex items-center gap-3">
            <CheckCircleIcon className="h-6 w-6 text-success flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-success">Video uploaded successfully</p>
              <p className="text-xs text-success/70 mt-0.5 truncate">{uploadedFileName}</p>
            </div>
            <button
              onClick={handleCancel}
              className="text-success/60 hover:text-success flex-shrink-0"
              aria-label="Upload a different file"
              title="Upload a different file"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
        </div>
      ) : !selectedFile ? (
        /* ── Idle / drop-zone state ──────────────────────────────────────── */
        <div
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          data-testid="drop-zone"
          role="button"
          aria-label="Upload video file"
          className={cn(
            'relative border-2 border-dashed rounded-lg p-12 text-center transition-colors',
            disabled && 'opacity-50 cursor-not-allowed',
            !disabled && isDragging && 'border-primary-500 bg-primary-50',
            !disabled && !isDragging && 'border-border-subtle hover:border-border-strong bg-dark-elevated-1'
          )}
        >
          <CloudArrowUpIcon
            data-testid="upload-icon"
            className={cn(
              'mx-auto h-12 w-12 transition-colors',
              isDragging ? 'text-accent' : 'text-text-muted'
            )}
          />
          <div className="mt-4">
            <button
              type="button"
              onClick={handleBrowseClick}
              className="text-primary-600 font-medium hover:text-primary-700 bg-transparent border-none cursor-pointer"
              disabled={isUploading || disabled}
            >
              Browse files
            </button>
            <input
              ref={fileInputRef}
              id="video-upload"
              type="file"
              className="sr-only"
              accept={acceptedFormats}
              onChange={handleFileInput}
              disabled={isUploading || disabled}
              aria-label="Choose video file to upload"
            />
            <span className="text-text-secondary"> or drag and drop</span>
          </div>
          <p className="mt-2 text-xs text-text-muted">
            MP4, AVI, MOV, WebM - Maximum size: {formatFileSize(maxFileSize)}
          </p>
        </div>
      ) : (
        /* ── File selected / uploading state ─────────────────────────────── */
        <div className="border border-border-subtle rounded-lg p-6 bg-dark-elevated-1">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h3 className="text-sm font-medium text-text-primary">
                {selectedFile.name}
              </h3>
              <p className="text-sm text-text-muted mt-1">
                {formatFileSize(selectedFile.size)}
              </p>
            </div>
            {!isUploading && (
              <button
                onClick={handleCancel}
                className="text-text-muted hover:text-text-secondary"
                aria-label="Remove file"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            )}
          </div>

          {isUploading && (
            <div className="mt-4">
              {showProgress ? (
                <>
                  <p className="text-sm text-text-secondary mb-2">Uploading...</p>
                  <Progress value={uploadProgress} showLabel label="Uploading..." />
                </>
              ) : (
                <div className="flex items-center gap-2">
                  <div className="h-1.5 flex-1 bg-dark-elevated-2 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-accent rounded-full transition-all duration-300"
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                  <span className="text-xs text-text-muted tabular-nums w-10 text-right">
                    {uploadProgress}%
                  </span>
                </div>
              )}
            </div>
          )}

          {!isUploading && (
            <div className="mt-4 flex gap-2">
              <Button
                onClick={handleUpload}
                disabled={isUploading}
                className="bg-accent hover:bg-accent-hover text-text-inverse font-semibold"
              >
                Upload
              </Button>
              <Button variant="outline" onClick={handleCancel}>
                Cancel
              </Button>
            </div>
          )}
        </div>
      )}

      {/* ── Status banners (only shown when there is actually a problem) ──── */}
      {validationError && (
        <Alert variant="error" className="mt-4">
          {validationError}
        </Alert>
      )}

      {error && (
        <Alert variant="error" className="mt-4">
          <div className="space-y-2">
            <p>Upload failed: {error.message}</p>
            <button
              type="button"
              onClick={handleCancel}
              className="text-xs underline text-danger hover:opacity-80"
            >
              Try again
            </button>
          </div>
        </Alert>
      )}
    </div>
  );
}

export default VideoUploader;
